import React from 'react';
import { Dropdown, Grid, Popup, Icon } from 'semantic-ui-react';
import { Field, FormikProps } from 'formik';
import { FieldLabel } from '../../../../styles';
import Input from '../../../../components/Input';
import { batchClosingDayOptions } from '../../types';
import { successNotification } from '../../../../services/notification';
import { unmaskMoney } from '../../../../services/masks';
import CoveragesConfigModal from './ModalContent';
import Toggle from '../../../../components/Toggle';

export type YelumFireInsuranceConfigProps = FormikProps<any> & {
  handleInputChange: Function;
};

const YELUM_TRANSMISSION_DAY = 3;

const sanitizePercentageInput = (value: string) => {
  const normalizedValue = String(value ?? '')
    .replace(/,/g, '.')
    .replace(/[^\d.]/g, '');
  const firstDecimalSeparatorIndex = normalizedValue.indexOf('.');

  if (firstDecimalSeparatorIndex === -1) {
    return normalizedValue;
  }

  const integerPart = normalizedValue.slice(0, firstDecimalSeparatorIndex);
  const decimalPart = normalizedValue
    .slice(firstDecimalSeparatorIndex + 1)
    .replace(/\./g, '')
    .slice(0, 2);

  return `${integerPart}.${decimalPart}`;
};

const formatPercentageInput = (value: string) => {
  const sanitizedValue = sanitizePercentageInput(value);

  if (!sanitizedValue || sanitizedValue === '.') {
    return '';
  }

  const numericValue = Number(sanitizedValue);

  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return Math.min(Math.max(numericValue, 0), 100).toFixed(2);
};

const YelumFireInsuranceConfig = (props: YelumFireInsuranceConfigProps) => {
  const { setFieldValue, handleInputChange } = props;
  const yelum = props?.values?.insurerSpecificData?.yelum?.fireInsurance;

  const handlePercentageChange = (data: any) => {
    setFieldValue(data.field.name, sanitizePercentageInput(data.value));
  };

  const handlePercentageBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.name, formatPercentageInput(e.target.value));
  };

  const handleDueDateChange = (value: number) => {
    setFieldValue('insurerSpecificData.yelum.fireInsurance.dueDate', value);
  };

  // A transmissão para a Yelum é sempre no dia 3, independente do vencimento
  React.useEffect(() => {
    if (yelum && yelum.lotClosingDay !== YELUM_TRANSMISSION_DAY) {
      setFieldValue(
        'insurerSpecificData.yelum.fireInsurance.lotClosingDay',
        YELUM_TRANSMISSION_DAY
      );
    }
  }, [yelum?.lotClosingDay]);

  return (
    <Grid>
      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>Código Parceiro *</FieldLabel>
          <Field
            value={yelum.code}
            name="insurerSpecificData.yelum.fireInsurance.code"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) => {
              handleInputChange(data, props);
            }}
            onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.code',
                e.target.value
              );
            }}
            component={Input}
            fluid
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>Comissão Corretora (%) *</FieldLabel>
          <Field
            value={yelum?.brokerCommission === 0 ? '' : yelum?.brokerCommission}
            name="insurerSpecificData.yelum.fireInsurance.brokerCommission"
            component={Input}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) =>
              handlePercentageChange(data)
            }
            onBlur={handlePercentageBlur}
            type="text"
            inputMode="decimal"
            fluid
          />
        </Grid.Column>
        <Grid.Column>
          <FieldLabel>Pró-labore (%) *</FieldLabel>
          <Field
            value={yelum?.proLabore === 0 ? '' : yelum?.proLabore}
            name="insurerSpecificData.yelum.fireInsurance.proLabore"
            component={Input}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) =>
              handlePercentageChange(data)
            }
            onBlur={handlePercentageBlur}
            type="text"
            inputMode="decimal"
            fluid
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>
            Data de Vencimento do Boleto *
            <Popup
              content={
                'Dia do mês em que o boleto vencerá (ex: dia 5, 10, 15). A transmissão para a Yelum é sempre no dia 3, independente do vencimento. O vencimento define apenas a janela de retroatividade (60 dias se até o dia 10, 30 dias se após).'
              }
              position={'top left'}
              trigger={
                <Icon
                  name={'info circle'}
                  color={'blue'}
                  style={{ cursor: 'help', marginLeft: 5 }}
                />
              }
            />
          </FieldLabel>
          <Dropdown
            style={{
              width: '100%'
            }}
            value={yelum?.dueDate}
            name={`insurerSpecificData.yelum.fireInsurance.dueDate`}
            options={batchClosingDayOptions}
            placeholder={'Selecionar...'}
            search
            selection
            onChange={(_, { value }) => {
              handleDueDateChange(value as number);
            }}
            clearable
          />
        </Grid.Column>
        <Grid.Column>
          <FieldLabel>
            Dia Preferencial de Transmissão *
            <Popup
              content={
                'A transmissão para a Yelum é sempre realizada no dia 3 de cada mês, independente do dia de vencimento do boleto.'
              }
              position={'top left'}
              trigger={
                <Icon
                  name={'info circle'}
                  color={'blue'}
                  style={{ cursor: 'help', marginLeft: 5 }}
                />
              }
            />
          </FieldLabel>
          <Dropdown
            style={{
              width: '100%'
            }}
            value={YELUM_TRANSMISSION_DAY}
            name={`insurerSpecificData.yelum.fireInsurance.lotClosingDay`}
            options={batchClosingDayOptions}
            placeholder={'Selecionar...'}
            search
            selection
            onChange={(_, { value }) => {
              setFieldValue(
                `insurerSpecificData.yelum.fireInsurance.lotClosingDay`,
                value
              );
            }}
            disabled
          />
        </Grid.Column>
      </Grid.Row>

      <Grid.Row columns={'equal'}>
        <Grid.Column>
          <FieldLabel>Comissão na Assistência?</FieldLabel>
          <Toggle
            name="insurerSpecificData.yelum.fireInsurance.applyCommissionOnAssistance"
            checked={true}
            disabled
            label={'SIM'}
          />
        </Grid.Column>
        <Grid.Column>
          <FieldLabel>
            Parcelamento Flexível
            <Popup
              content={
                <div style={{ maxWidth: '300px', lineHeight: '1.4' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                    O que muda?
                  </p>
                  <p style={{ margin: '0' }}>
                    <strong>Desativado:</strong> O seguro será sempre cobrado em 12x fixo.<br />
                    <strong>Ativado:</strong> A imobiliária pode escolher cobrar em menos parcelas (1x a 12x)
                    e fazer a conciliação pelo módulo de Faturas.
                  </p>
                </div>
              }
              position={'top left'}
              trigger={
                <Icon
                  name={'info circle'}
                  color={'blue'}
                  style={{ cursor: 'help', marginLeft: 5 }}
                />
              }
            />
          </FieldLabel>
          <Toggle
            name="insurerSpecificData.yelum.fireInsurance.flexibleInstallmentsEnabled"
            checked={yelum?.flexibleInstallmentsEnabled}
            onChange={() => {
              setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.flexibleInstallmentsEnabled',
                !yelum?.flexibleInstallmentsEnabled
              );
            }}
            label={yelum?.flexibleInstallmentsEnabled ? 'SIM' : 'NÃO'}
          />
        </Grid.Column>
        <Grid.Column>
          <FieldLabel>
            Orçamento Consultivo
            <Popup
              content={
                <div style={{ maxWidth: '300px', lineHeight: '1.4' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                    O que muda?
                  </p>
                  <p style={{ margin: '0' }}>
                    <strong>Ativado:</strong> a imobiliária poderá solicitar orçamentos
                    consultivos (ex.: indústrias e atividades fora do padrão). O valor é
                    definido pelo nosso time após a análise da seguradora e a contratação
                    é feita manualmente, sem integração automática com a Yelum.
                  </p>
                </div>
              }
              position={'top left'}
              trigger={
                <Icon
                  name={'info circle'}
                  color={'blue'}
                  style={{ cursor: 'help', marginLeft: 5 }}
                />
              }
            />
          </FieldLabel>
          <Toggle
            name="insurerSpecificData.yelum.fireInsurance.consultativeQuoteEnabled"
            checked={yelum?.consultativeQuoteEnabled}
            onChange={() => {
              setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.consultativeQuoteEnabled',
                !yelum?.consultativeQuoteEnabled
              );
            }}
            label={yelum?.consultativeQuoteEnabled ? 'SIM' : 'NÃO'}
          />
        </Grid.Column>
      </Grid.Row>

      <Grid.Row columns={'equal'}>
        <Grid.Column width={16}>
          <CoveragesConfigModal {...props} />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default YelumFireInsuranceConfig;
